import * as Sentry from '@sentry/vue';
import {
  IDeleteReason,
  IDonationData,
  IRemoteConfig,
  IUninstallReason,
} from 'components/models';
import { defineStore } from 'pinia';
import { getDonationData, getRemoteConfig } from 'src/services/appService';
import { searchSuggestions } from 'src/services/searchService';

export interface IAppStoreState {
  mealCount: number;
  loading: boolean;
  uninstallReasons: IUninstallReason[];
  deleteReasons: IDeleteReason[];
  donationData: IDonationData[];
  searchSuggestions: Record<
    STORAGE_KEYS,
    {
      loading: boolean;
      data: string[];
    }
  >;
}

export enum STORAGE_KEYS {
  SEARCH_FIELD = 'SEARCH_FIELD',
  SEARCH_PAGE = 'SEARCH_PAGE',
}

export const useAppStore = defineStore('app', {
  state: () => {
    return {
      mealCount: 0,
      loading: false,
      uninstallReasons: [],
      deleteReasons: [],
      donationData: [],
      searchSuggestions: {
        [STORAGE_KEYS.SEARCH_FIELD]: {
          data: [],
          loading: false,
        },
        [STORAGE_KEYS.SEARCH_PAGE]: {
          data: [],
          loading: false,
        },
      },
    } as IAppStoreState;
  },
  getters: {
    isLoadingSuggestions: (state) => {
      return (storageKey: STORAGE_KEYS) =>
        !!state.searchSuggestions[storageKey]?.loading;
    },
    suggestionsData: (state) => {
      return (storageKey: STORAGE_KEYS) =>
        state.searchSuggestions[storageKey]?.data ?? [];
    },
  },
  actions: {
    async getSearchSuggestions(keyword: string, storageKey: STORAGE_KEYS) {
      if (!keyword?.trim()?.length) {
        return (this.searchSuggestions[storageKey].data = []);
      }

      this.searchSuggestions[storageKey].loading = true;
      try {
        const { data } = await searchSuggestions(keyword);

        this.searchSuggestions[storageKey].data = [
          ...new Set(data.slice(0, 5)),
        ];
        return;
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        this.searchSuggestions[storageKey].loading = false;
      }
    },
    async setRemoteConfig() {
      if (this.mealCount) {
        return;
      }

      try {
        this.loading = true;
        const { data } = (await getRemoteConfig()) as { data: IRemoteConfig };
        this.mealCount = data?.mealCounter ?? 0;
        this.uninstallReasons = data?.uninstallReasons ?? [];
        this.deleteReasons = data?.deleteReasons ?? [];
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        this.loading = false;
      }
    },
    async getDonationData() {
      if (this.donationData.length) {
        return;
      }

      try {
        this.loading = true;
        const { data } = (await getDonationData()) as { data: IDonationData[] };
        this.donationData = data;
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        this.loading = false;
      }
    },
  },
});
